import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import {
  LayoutDashboard,
  Search,
  FolderOpen,
  LogOut,
  ChevronRight,
  Library,
  Shield,
  User,
  Import,
  HelpCircle,
  Hash,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import FeedbackWidget from "./FeedbackWidget";

const DashboardLayout = ({ children }) => {
  const { logout, user } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/search", icon: Search, label: "Single Song Search" },
    { path: "/searches", icon: Library, label: "All Searches" },
    { path: "/batches", icon: FolderOpen, label: "Batch CSV" },
    { path: "/import-catalogue", icon: Import, label: "Import Catalogue" },
    { path: "/code-lookup", icon: Hash, label: "ISRC / ISWC Lookup" },
    { path: "/catalogue-matching", icon: FileSpreadsheet, label: "Catalogue Matching" },
    { path: "/help", icon: HelpCircle, label: "Help Centre" },
    // Only show Admin for admin users
    ...(user?.is_admin ? [{ path: "/admin", icon: Shield, label: "Admin" }] : []),
  ];

  const getBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter(Boolean);
    if (paths.length === 0) return [{ label: "Dashboard", path: "/" }];
    
    const breadcrumbs = [{ label: "Dashboard", path: "/" }];
    
    if (paths[0] === "search" && paths[1]) {
      breadcrumbs.push({ label: "All Searches", path: "/searches" });
      breadcrumbs.push({ label: `#${paths[1].slice(0, 8)}...`, path: location.pathname });
    } else if (paths[0] === "search") {
      breadcrumbs.push({ label: "Single Song Search", path: "/search" });
    } else if (paths[0] === "searches") {
      breadcrumbs.push({ label: "All Searches", path: "/searches" });
    } else if (paths[0] === "batches") {
      breadcrumbs.push({ label: "Batch CSV", path: "/batches" });
    } else if (paths[0] === "batch" && paths[1]) {
      breadcrumbs.push({ label: "Batch CSV", path: "/batches" });
      breadcrumbs.push({ label: `Batch #${paths[1].slice(0, 8)}...`, path: location.pathname });
    } else if (paths[0] === "admin") {
      breadcrumbs.push({ label: "Admin Dashboard", path: "/admin" });
    } else if (paths[0] === "import-catalogue") {
      breadcrumbs.push({ label: "Import Catalogue", path: "/import-catalogue" });
    } else if (paths[0] === "code-lookup") {
      breadcrumbs.push({ label: "ISRC / ISWC Lookup", path: "/code-lookup" });
    } else if (paths[0] === "catalogue-matching") {
      breadcrumbs.push({ label: "Catalogue Matching", path: "/catalogue-matching" });
    } else if (paths[0] === "help") {
      breadcrumbs.push({ label: "Help Centre", path: "/help" });
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="flex h-screen overflow-hidden" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-syncchain-paper border-r border-white/10 flex flex-col">
        {/* Logo */}
        <div className="h-20 flex items-center px-4 border-b border-white/10">
          <img 
            src="https://customer-assets.emergentagent.com/job_87e61a64-90d2-4c20-8ba6-31392dc5e379/artifacts/xzzkl8ic_SyncChain_White.png" 
            alt="SyncChain Logo" 
            className="h-10 w-auto"
          />
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                    isActive
                      ? "bg-syncchain-accent/10 text-syncchain-accent"
                      : "text-syncchain-text-secondary hover:bg-white/5 hover:text-syncchain-text"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium text-sm">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* User Info & Footer */}
        <div className="border-t border-white/10">
          {/* User Profile */}
          {user && !user.legacy && (
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-syncchain-accent/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-syncchain-accent font-semibold text-sm">
                    {user.name?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-syncchain-text truncate">{user.name}</p>
                    {user.is_admin && (
                      <Shield className="w-3.5 h-3.5 text-syncchain-accent flex-shrink-0" />
                    )}
                  </div>
                  <Badge variant="outline" className="mt-1 text-[10px] px-1.5 py-0 h-4 bg-white/5 border-white/10 text-syncchain-text-muted">
                    {user.role}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          
          {/* Logout Button */}
          <div className="p-4">
            <Button
              variant="ghost"
              className="w-full justify-start text-syncchain-text-secondary hover:text-syncchain-error hover:bg-red-500/10"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden bg-syncchain-bg">
        {/* Top Bar */}
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 bg-syncchain-paper/50 backdrop-blur-sm">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm">
            {breadcrumbs.map((crumb, idx) => (
              <div key={crumb.path} className="flex items-center gap-2">
                {idx > 0 && <ChevronRight className="w-4 h-4 text-syncchain-text-muted" />}
                <NavLink
                  to={crumb.path}
                  className={`${
                    idx === breadcrumbs.length - 1
                      ? "text-syncchain-text font-medium"
                      : "text-syncchain-text-secondary hover:text-syncchain-text"
                  } transition-colors`}
                >
                  {crumb.label}
                </NavLink>
              </div>
            ))}
          </div>
        </header>

        {/* Content Area */}
        <ScrollArea className="flex-1">
          <div className="p-6 md:p-8 animate-fade-in">
            {children}
          </div>
        </ScrollArea>
      </main>
      
      {/* Feedback Widget */}
      <FeedbackWidget />
    </div>
  );
};

export default DashboardLayout;
